import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Sparkles, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labelClass = "font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 block mb-2";

const FUNNEL_STEPS = [
  ["home_view", "Visits"],
  ["cta_click", "CTA clicks"],
  ["pricing_view", "Pricing views"],
  ["checkout_start", "Checkouts"],
  ["checkout_success", "Paid"],
];

export const PerformanceTab = ({ campaign, headers, onCampaign }) => {
  const [perf, setPerf] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wingman/campaigns/${campaign.campaign_id}/performance`, { headers: headers(), withCredentials: true });
      setPerf(res.data);
    } catch {
      setError("Could not load performance data.");
    }
  }, [campaign.campaign_id, headers]);

  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    if (analyzing) return;
    setAnalyzing(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/wingman/campaigns/${campaign.campaign_id}/recommend`, {}, { headers: headers(), withCredentials: true, timeout: 120000 });
      onCampaign({ ...campaign, recommendation: res.data });
    } catch (e) {
      setError(e.response?.data?.detail || "Analysis failed — try again.");
    }
    setAnalyzing(false);
  };

  const funnel = perf?.funnel || {};
  const recommendation = campaign.recommendation;

  return (
    <div className="space-y-6" data-testid="tab-panel-performance">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="performance-funnel">
        {FUNNEL_STEPS.map(([key, label]) => (
          <div key={key} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4" data-testid={`perf-${key}`}>
            <span className={labelClass}>{label}</span>
            <span className="text-2xl text-white font-medium">{funnel[key] || 0}</span>
          </div>
        ))}
        <div className="bg-[#0A0A0A] border border-emerald-400/20 rounded-lg p-4" data-testid="perf-revenue">
          <span className={labelClass}>Est. revenue</span>
          <span className="text-2xl text-emerald-400 font-medium">${perf?.revenue_estimate_usd || 0}</span>
        </div>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500" data-testid="performance-note">
        Live events tagged with this campaign's UTM links — revenue is an estimate from tracked plan checkouts
      </p>

      {perf && perf.sources.length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6" data-testid="performance-sources">
          <h3 className="font-display text-xl text-white mb-5">By channel</h3>
          <div className="space-y-2">
            <div className="grid grid-cols-5 gap-3 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 px-3">
              <span>Source</span><span>Visits</span><span>CTA</span><span>Checkouts</span><span>Paid</span>
            </div>
            {perf.sources.map((s) => (
              <div key={s.source} className="grid grid-cols-5 gap-3 border border-white/10 rounded-md p-3 text-sm" data-testid={`source-${s.source}`}>
                <span className="text-white font-medium">{s.source}</span>
                <span className="text-zinc-300">{s.visits}</span>
                <span className="text-zinc-300">{s.cta_clicks}</span>
                <span className="text-zinc-300">{s.checkouts}</span>
                <span className="text-emerald-400">{s.paid}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {perf && perf.sources.length === 0 && (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6" data-testid="performance-empty">
          <p className="text-sm text-zinc-300 leading-relaxed">
            No UTM-tagged traffic yet. Publish the assets with the links from the UTM tab and this panel
            turns into a live funnel — visits through paid conversions, per channel.
          </p>
        </div>
      )}

      {perf && (perf.sends || []).length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6" data-testid="performance-sends">
          <h3 className="font-display text-xl text-white mb-4">Subscriber sends</h3>
          <div className="space-y-2">
            {[...perf.sends].reverse().map((s, i) => (
              <div key={`${s.at}-${i}`} className="flex items-center gap-4 border border-white/10 rounded-md p-3" data-testid={`send-log-${i}`}>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-white truncate">{s.subject}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">{new Date(s.at).toLocaleString()}</span>
                </span>
                <span className="font-mono text-xs text-zinc-300 shrink-0">{s.delivered}/{s.recipients} delivered{s.failed ? ` · ${s.failed} failed` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6" data-testid="recommendation-card">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <h3 className="font-display text-xl text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" strokeWidth={1.5} /> Where to double down
          </h3>
          <button type="button" onClick={analyze} disabled={analyzing} data-testid="performance-analyze-button"
            className="h-10 px-5 rounded-full v4-btn-primary text-sm font-semibold flex items-center gap-2 disabled:opacity-40 transition-colors duration-300">
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            {analyzing ? "Analyzing…" : recommendation ? "Re-analyze" : "Analyze with Wingman"}
          </button>
        </div>
        {error && <p className="font-mono text-xs text-[#FF3333] mb-4" data-testid="performance-error">{error}</p>}
        {recommendation ? (
          <div data-testid="recommendation-result">
            <p className="text-sm text-white leading-relaxed mb-5">{recommendation.headline}</p>
            <div className="space-y-3 mb-5">
              {(recommendation.recommendations || []).map((r, i) => (
                <div key={i} className="border border-white/10 rounded-md p-4" data-testid={`recommendation-${i}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-400 block mb-1.5">{r.channel}</span>
                  <p className="text-sm text-white mb-1">{r.action}</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">{r.why}</p>
                </div>
              ))}
            </div>
            {recommendation.next_experiment && (
              <p className="text-sm text-zinc-300 leading-relaxed border-t border-white/10 pt-4" data-testid="next-experiment">
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 block mb-1.5">Next experiment</span>
                {recommendation.next_experiment}
              </p>
            )}
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-4">
              Analyzed {new Date(recommendation.at).toLocaleString()}
            </p>
          </div>
        ) : (
          !analyzing && <p className="text-sm text-zinc-400">Run the analysis once traffic lands — Wingman reads the funnel and tells you where to spend the next hour.</p>
        )}
      </div>
    </div>
  );
};
